/**
 * Which failed upload a retry can continue instead of starting over. Kept free
 * of store/network imports so the standalone test runner can exercise it
 * (UploadManager pulls in the whole store/network graph).
 */
import type { Media } from '@typings/Media';
import type { FileUploadStatus, UploadDestination, UploadFinishData } from './UploadManager';

/** A failed upload whose multipart session may still be open on the server, with
 * the parts it landed. The backend signs part URLs for 7 days and assembles
 * whatever parts the session holds on finish, so continuing it only needs the
 * parts that never landed. */
export type ResumableUpload = {
  /** The failed `uploadStatus` entry the retry takes over from. */
  previousUuid: string;
  media: Media;
  uploadFinishData: UploadFinishData;
  completedParts: number[];
  fingerprint?: string;
};

/** The newest failed upload of this same `File` into this same destination that
 * got as far as creating its media. */
export function findResumableUpload(
  entries: Iterable<FileUploadStatus>,
  file: File,
  destination: UploadDestination,
): ResumableUpload | undefined {
  let newest: FileUploadStatus | undefined;
  for (const entry of entries) {
    if (
      entry.file === file &&
      entry.status === 'error' &&
      entry.media?._id &&
      entry.uploadFinishData &&
      entry.destination.libraryId === destination.libraryId &&
      entry.destination.folderPath === destination.folderPath &&
      (!newest || entry.createdAt > newest.createdAt)
    ) {
      newest = entry;
    }
  }
  if (!newest?.media || !newest.uploadFinishData) return undefined;
  return {
    previousUuid: newest.uuid,
    media: newest.media,
    uploadFinishData: newest.uploadFinishData,
    completedParts: newest.completedParts ?? [],
    fingerprint: newest.fingerprint,
  };
}
